import {
    LEGACY_PLACEHOLDER_KEYS,
    PLACEHOLDERS_KEY,
    SPLIT_TESTING_KEY,
    USER_ID_KEY,
} from '$lib/global/constants';

import {
    hasConsentDecision,
    isFirstPartyTrackingAllowed,
    isTrackingAllowed,
    onConsentDecision,
    onConsentGranted,
    onConsentResolved,
} from '$lib/global/consent/consentGate';
import loadAnalyticsWrapper, { updateSegmentTraitsKey } from '$lib/global/events/loadAnalytics';
import getQueryParameter from '$lib/global/helper/get-query-parameter';
import { handleAnonId } from '$lib/global/storage';
import { seedTrackingPropertiesFromQuery } from '$lib/Stores/seedTrackingProperties';
import { embedOptions } from '$lib/Stores/stores';
import { clearUserAnswers } from '$lib/utils/userAnswerStorage';

import { nanoid } from 'nanoid/non-secure';
import { get } from 'svelte/store';

import { getNowISO } from './helper';
import { integrations as trackingIntegrations } from './integrations';
import { ensureAnalyticsStub, getTrackingTitles } from './tracking';
import { serializeForPipeline } from './TrackingContext';
import { TrackingSession } from './TrackingSession';
import { V3Strategy, V4Strategy } from './TrackingStrategy';
import { campaignLoad } from './v4';
import psProperties from './v4.ps-properties';
import { updateVisitorProperties } from './v4.visitor-properties';

import { trackPage } from './index';

import type { Field } from '$lib/types/Field';
import type { LayoutPageEntry } from '$lib/types/LayoutData';
import type { Page } from '$lib/types/Page';
import type { PageContext, PipelineContext } from './TrackingContext';
import type { TrackingStrategy } from './TrackingStrategy';

// ── Config type ──────────────────────────────────────────────────────────

export interface TrackingServiceConfig {
    campaignId: string;
    companyId: string;
    workspaceId: string;
    campaignVersionId: string;
    trackingVersion: string | undefined;
    preventTracking: boolean;
    analyticsLink: string;
}

/**
 * A navigation that happened while consent was still pending.
 *
 * Route changes and page views are kept in one ordered queue rather than two lists: replaying
 * all route changes first and all page views afterwards would attribute the entry page's view
 * to whichever page the visitor ended up on.
 */
type PendingNavigation =
    | { type: 'route'; slug: string | undefined; pageData: PageContext }
    | { type: 'page'; pageData: Page };

/**
 * Upper bound on buffered navigations. A page with `automaticRedirect` enabled navigates on a
 * timer, so a funnel could advance indefinitely while the banner sits open. The earliest
 * events are the ones worth keeping — they carry entry-page attribution — so this drops the
 * newest once the cap is hit rather than evicting the oldest.
 */
const MAX_PENDING_NAVIGATIONS = 50;

// ── TrackingService ──────────────────────────────────────────────────────

export class TrackingService {
    // Identity
    private _userId: string;

    // Campaign config (immutable after construction)
    private readonly config: TrackingServiceConfig;

    // Page map from layout
    private readonly pages: LayoutPageEntry[];

    // Tracking titles (funnel-level, computed once from v4 fields)
    private _trackingTitles: Record<string, string> = {};

    // Query parameters (parsed once at init, reused on session regeneration)
    private _utm: Record<string, string> = {};
    private _custom: Record<string, string> = {};
    private _thirdParty: Record<string, string> = {};

    private pageContext: PageContext | null = null;

    // Page view dedup (inlined from TrackingPageView)
    private prevPageId: string | null = null;
    private prevPageName: string | null = null;

    // Bootstrap guard (inlined from TrackingInit)
    private _bootstrapped = false;

    // Activation guard — the consent-gated half of bootstrap (CAP-4147)
    private _activated = false;
    private _segmentLoaded = false;

    // Navigations that arrived while consent was still pending, replayed in order on
    // activation so a visitor who accepts still produces a complete first session.
    private pendingNavigations: PendingNavigation[] = [];

    // Composites
    private session: TrackingSession;

    // Strategy
    private strategy: TrackingStrategy | null = null;

    // A/B variant selection (null = no variant in funnel)
    private _variant: 'A' | 'B' | null = null;

    constructor(config: TrackingServiceConfig, pages: LayoutPageEntry[]) {
        this.config = config;
        this.pages = pages;

        // CAP-4147: reading or writing the persistent visitor id is itself a storage
        // access under § 25 TDDDG, so identity is resolved lazily by the `userId` getter
        // and only persisted once consent is granted (see resolvePersistentUserId).
        this._userId = '';

        // Build composites
        this.session = new TrackingSession();
    }

    // ── Public API: bootstrap (one-time init, called from layout) ───────

    // `fields` is optional: the root layout bootstraps on every route, and non-funnel
    // routes such as `/consent-policy` carry no fields (CAP-4251).
    bootstrap(fields?: Field[]): void {
        if (this._bootstrapped) return;
        this._bootstrapped = true;

        this.strategy = this.config.trackingVersion === 'v3' ? new V3Strategy() : new V4Strategy();

        // Parse and store query parameters (once — SvelteKit soft nav preserves search params)
        const { utm, custom, thirdParty, additionalTrackingTitles } = getQueryParameter();
        this._utm = utm;
        this._custom = custom;
        this._thirdParty = thirdParty;

        // Build tracking titles from fields + query param names (v4 only)
        if (this.isV4()) {
            this._trackingTitles = {
                ...getTrackingTitles(fields),
                ...additionalTrackingTitles,
            };
        }

        // Safety net — ensure analytics stub exists (no-ops if already created).
        // In-memory only, so it is safe before consent — and it buffers any early
        // window.analytics.* call instead of letting it hit the network.
        ensureAnalyticsStub();

        // The A/B variant decides which page content renders, so it must be picked
        // before the first paint. Picked in memory here; persisted on activation, which
        // with `MEASURE_BEFORE_DECISION` happens on the next line.
        this.resolveVariant();

        // Our own measurement, without analytics.js and without any third-party integration.
        // With `MEASURE_BEFORE_DECISION` this fires synchronously, so the visit is counted
        // from load rather than from the moment the banner is answered — otherwise a visitor
        // who never answers produces no session at all. Nothing here writes a cookie, a
        // localStorage entry or a persistent identifier; that all still waits for consent.
        onConsentResolved(() => this.activate());

        // Nothing is written to the visitor's device until they answer the banner, so this is
        // where the visit stops being memory-only: the session id, its traits and the A/B
        // assignment are flushed out, and the visitor id gets a store to live in. Until then a
        // reload deliberately starts a fresh visit rather than recognising the old one.
        onConsentDecision(() => {
            this.session.setPersistenceEnabled(true);
            this.session.flushToStorage(this.config.campaignId);
            this.resolvePersistentUserId();
            this.persistVariant();
        });

        // Separate again: only an opt-in may load analytics.js and move the visitor id from
        // sessionStorage to localStorage.
        onConsentGranted(() => {
            this.resolvePersistentUserId();
            this.ensureSegmentLoaded();
        });
    }

    // ── Consent-gated activation ────────────────────────────────────────

    /**
     * The half of bootstrap that may only run once the banner is answered: persist the
     * visitor id and A/B assignment, load analytics.js *if consent was given*, then replay
     * whatever navigation happened while the banner was still open.
     */
    private activate(): void {
        if (this._activated) return;
        this._activated = true;

        this.resolvePersistentUserId();
        this.persistVariant();

        this.ensureSegmentLoaded();

        // Drain before replaying: isTrackingAllowed() is already true here, so the replayed
        // calls take the normal path instead of re-buffering.
        const buffered = this.pendingNavigations;
        this.pendingNavigations = [];

        buffered.forEach((navigation) => {
            if (navigation.type === 'route') {
                this.handleRouteChange(navigation.slug, navigation.pageData);
            } else {
                this.trackPageTransition(navigation.pageData);
            }
        });
    }

    private bufferPendingNavigation(navigation: PendingNavigation): void {
        if (this.pendingNavigations.length >= MAX_PENDING_NAVIGATIONS) return;

        this.pendingNavigations.push(navigation);
    }

    /**
     * Where the visitor id is kept.
     *
     * With consent: localStorage, so a returning visitor keeps their identity across visits.
     * After a rejection: sessionStorage, which dies with the tab. The id is still needed
     * *within* the visit — it is the `globalId` on every event — but recognising the same
     * person on a later visit is precisely what the visitor declined, so nothing persistent
     * is written for them.
     */
    private visitorIdStore(): Storage | null {
        try {
            return isTrackingAllowed() ? window.localStorage : window.sessionStorage;
        } catch {
            // Storage unavailable (Safari private mode, cross-origin iframe)
            return null;
        }
    }

    private readStoredUserId(): string | null {
        try {
            return this.visitorIdStore()?.getItem(USER_ID_KEY) ?? null;
        } catch {
            return null;
        }
    }

    /**
     * Persist the visitor id on activation, to whichever store the consent decision allows.
     *
     * First read wins. If nothing has observed the id yet, an id from a previous consented
     * visit is preferred so returning visitors keep their identity. But once the ephemeral
     * pre-consent id has been handed out it is kept and persisted, because consumers capture
     * it eagerly — Embed.Controller bakes it into the HTML embed's `globalVariables` at render
     * time, and OtpVerificationModal.Controller destructures it — and swapping it afterwards
     * would leave their copy pointing at a different visitor than our events.
     *
     * This matters more than it looks: `perspective__user__id` is a single global key while
     * the consent decision is scoped to campaign + version, so republishing a funnel shows the
     * banner again to visitors who still carry an id from before.
     *
     * Also called on a later grant, which promotes a rejecting visitor's tab-scoped id to
     * localStorage once they opt in.
     */
    private resolvePersistentUserId(): void {
        if (!this._userId) {
            this._userId = this.readStoredUserId() || nanoid();
        }

        // Held in memory until the visitor answers the banner, like the session id.
        if (!hasConsentDecision()) return;

        try {
            this.visitorIdStore()?.setItem(USER_ID_KEY, this._userId);

            // Promoted to localStorage by a later grant — drop the tab-scoped copy so the id
            // does not exist in two places with a chance of diverging.
            if (isTrackingAllowed()) {
                window.sessionStorage.removeItem(USER_ID_KEY);
            }
        } catch {
            // Storage unavailable
        }
    }

    private resolveVariant(): void {
        if (!this.pages.some((p) => p.isVariant)) return;

        const stored = isFirstPartyTrackingAllowed()
            ? window.sessionStorage.getItem(SPLIT_TESTING_KEY + this.config.campaignId)
            : null;

        if (stored === 'A' || stored === 'B') {
            this._variant = stored;

            return;
        }

        this._variant = Math.random() < 0.5 ? 'A' : 'B';
    }

    /**
     * Write the selected variant to sessionStorage on activation.
     *
     * The in-memory selection is authoritative and storage follows it, never the reverse.
     * resolveVariant() runs before the first paint, so by the time consent arrives `_variant`
     * has already decided what rendered and what any buffered page view described. Adopting a
     * differing stored value here would swap the visitor's content on the next update — the
     * very mid-session switch this is meant to prevent.
     *
     * A stale entry is reachable because the split key is scoped to campaignId while the
     * consent decision is scoped to campaignId + campaignVersionId, so republishing a funnel
     * resets consent without clearing the variant.
     */
    private persistVariant(): void {
        if (!this._variant) return;

        // Nothing on the device before the visitor answers the banner. A reload before that
        // starts a new visit anyway, so re-picking the variant then is consistent.
        if (!hasConsentDecision()) return;

        window.sessionStorage.setItem(SPLIT_TESTING_KEY + this.config.campaignId, this._variant);
    }

    // ── Public API: handleRouteChange (called from layout afterNavigate) ─

    handleRouteChange(slug: string | undefined, pageData: PageContext): void {
        // Skip if slug doesn't match any known page
        if (slug && !this.pages.some((p) => p.slug === slug)) return;

        // CAP-4545: nor is a route without a page id a funnel page, even with no slug to
        // check. The root layout calls this on every route, so `/consent-policy` and the
        // error page a failed page load renders (both carry layout data only) would
        // otherwise count as the homepage — starting a session and sending `Funnel Loaded`
        // that metrics cannot attribute to a page, and drops.
        if (!pageData.pageId) return;

        // Update page context BEFORE session resolution so that createNewSession
        // has the correct page data for initVisitorProperties (ps_start_slug, ps_start_page).
        // In-memory only, and other components read it via the pageSlug/pageId getters,
        // so this stays outside the consent gate.
        this.pageContext = pageData;

        // Session resolution emits campaignLoad and, once the banner is answered, writes
        // sessionStorage. Hold it until first-party measurement is permitted, then replay
        // from activate().
        if (!isFirstPartyTrackingAllowed()) {
            this.bufferPendingNavigation({ type: 'route', slug, pageData });

            return;
        }

        const isHomePage = this.resolveIsHomePage(slug);
        this.resolveSession(isHomePage);
        this.session.setCurrentPage(slug ?? '', isHomePage);
    }

    // ── Public API: trackPageTransition (called from Page.svelte) ────────

    trackPageTransition(pageData: Page): void {
        // CAP-4147: buffer the page view until the banner is answered, so the entry page is
        // recorded either way once we know the visitor's choice.
        if (!isFirstPartyTrackingAllowed()) {
            this.bufferPendingNavigation({ type: 'page', pageData });

            return;
        }

        trackPage({
            pageName: pageData.pageName,
            tag: pageData.tag,
            campaignId: pageData.campaignId,
            pageSlug: pageData.pageSlug,
            isLastPage: pageData.isLastPage,
            pageId: pageData.pageId,
            fromPageId: this.prevPageId,
            fromPageName: this.prevPageName,
        });
        this.prevPageId = pageData.pageId;
        this.prevPageName = pageData.pageName;
    }

    /**
     * Load Segment's analytics.js, once, and only with real consent.
     *
     * Loading it is what creates the `ajs_*` cookies and what makes third-party integrations
     * reachable at all. A rejecting visitor's events go out through `firstPartyDispatch`
     * instead, which needs none of it — so for them this never runs.
     */
    private ensureSegmentLoaded(): void {
        if (this._segmentLoaded) return;

        if (!isTrackingAllowed()) return;

        const analytics: any = window.analytics;

        if (analytics?.initialize) return;

        this._segmentLoaded = true;

        void loadAnalyticsWrapper({
            companyId: this.config.companyId,
            integrations: { Perspective: !!trackingIntegrations.perspective },
            preventTracking: this.config.preventTracking,
            analyticsLink: this.config.analyticsLink,
        });
    }

    // ── Page map helpers ────────────────────────────────────────────────

    private resolveIsHomePage(slug?: string): boolean {
        if (!slug) return true; // root route = homepage
        const entry = this.pages.find((p) => p.slug === slug);
        return entry?.isHomePage ?? false;
    }

    // ── Session resolution ───────────────────────────────────────────────

    private resolveSession(isHomePage: boolean): void {
        const urlSessionId = new URLSearchParams(window.location.search).get('sessionId');
        const hasUrlSessionId = urlSessionId !== null;

        // Soft nav — session already in memory
        if (this.session.getSessionId()) {
            if (hasUrlSessionId && urlSessionId !== this.session.getSessionId()) {
                this.createNewSession();
                return;
            }

            if (isHomePage && this.session.shouldRegenerateForHomepage()) {
                this.createNewSession();
                return;
            }

            // Keep current session (covers same urlSessionId or no urlSessionId)
            return;
        }

        // Hard load — try URL sessionId first
        if (hasUrlSessionId) {
            const peeked = TrackingSession.peekBySessionId(urlSessionId);

            if (
                !peeked || // No traits to restore OR
                (isHomePage && peeked.hasSeenAnyNonHomepage) // Regenerate for homepage if seen non-homepage
            ) {
                this.createNewSession(); // Fall through to createNewSession
                return;
            }

            this.session.tryRestoreFromSessionId(this.config.campaignId, urlSessionId);
            this.adoptRestoredSession();

            return;
        }

        // Hard load, no URL sessionId — try to restore from sessionStorage
        const stored = TrackingSession.peek(this.config.campaignId);

        if (stored) {
            if (isHomePage && stored.hasSeenAnyNonHomepage) {
                this.createNewSession();
                return;
            }

            // Restore the stored session into this.session
            this.session.tryRestore(this.config.campaignId);
            this.adoptRestoredSession();

            return;
        }

        // CLO-2097: sessionStorage is empty, but this may still be an interrupted visit
        // rather than a new one — a restored in-app WebView comes back without it. Only for a
        // non-homepage landing: arriving on the entry page is how a *new* journey starts
        // (`shouldRegenerateForHomepage` applies the same rule within a session), and it is
        // also the arrival a second person on a shared device is most likely to make.
        if (!isHomePage) {
            const resumable = TrackingSession.peekResume(this.config.campaignId);

            if (resumable) {
                this.session.restoreSession(this.config.campaignId, resumable);
                this.adoptRestoredSession();

                return;
            }
        }

        // Nothing to restore — first visit
        this.createNewSession();
    }

    /**
     * Finish adopting a session that already existed, however it was found.
     *
     * The counterpart to `createNewSession` for the three restore branches: point Segment at
     * the session's traits key and merge this load's URL params into its visitor properties.
     * Deliberately does not fire `campaignLoad` — that is what distinguishes continuing a
     * visit from starting one.
     */
    private adoptRestoredSession(): void {
        updateSegmentTraitsKey(this.sessionId);

        if (this.isV4()) {
            this.initVisitorProperties();
        }
    }

    private createNewSession(): void {
        this.session = new TrackingSession();
        // Memory-only until the banner is answered; the onConsentDecision hook in bootstrap()
        // flushes it out at that point.
        this.session.setPersistenceEnabled(hasConsentDecision());
        this.session.create(this.config.campaignId);
        this.resetAnalytics();
        this.clearPersonalizationData();
        this.seedTrackingPropertiesFromUrl();

        // Seed visitor properties for the new session (v4 only).
        if (this.isV4()) {
            this.initVisitorProperties();
            campaignLoad(this.pageContext?.pageSlug || '', this.pageContext?.pageName || '');
        }
    }

    private clearPersonalizationData(): void {
        clearUserAnswers(this.config.campaignVersionId);
        window.sessionStorage.removeItem(`${PLACEHOLDERS_KEY}${this.config.campaignId}`);
        LEGACY_PLACEHOLDER_KEYS.forEach((prefix) => {
            window.sessionStorage.removeItem(`${prefix}${this.config.campaignId}`);
        });
    }

    /**
     * Re-seed personalization from URL query parameters (ps_* / perspective_* and utm_*)
     * after session creation clears it. This ensures that custom variables passed via GET
     * parameters survive across page navigations within the funnel.
     *
     * Delegates to the same helper the layout seeds from on mount, so the legacy-key
     * migration and the seeding rules live in exactly one place.
     */
    private seedTrackingPropertiesFromUrl(): void {
        seedTrackingPropertiesFromQuery(this.config.campaignId);
    }

    private initVisitorProperties(): void {
        const visitorProperties = this.session.getVisitorProperties();

        updateVisitorProperties({
            [psProperties.firstSeenAt]: visitorProperties[psProperties.firstSeenAt] || getNowISO(),
            [psProperties.startSlug]:
                visitorProperties[psProperties.startSlug] || this.pageContext?.pageSlug || '',
            [psProperties.startPage]:
                visitorProperties[psProperties.startPage] || this.pageContext?.pageName || '',
            ...this._utm,
            ...this._custom,
            ...this._thirdParty,
            [psProperties.isEmbed]: get(embedOptions).isEmbed ?? false,
            [psProperties.timezone]: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }

    private resetAnalytics(): void {
        // All three calls are Segment-specific: resetting analytics.js's identity, seeding its
        // anonymousId and re-scoping its traits key. None of it means anything without
        // analytics.js — and `handleAnonId()` writes the visitor id straight to localStorage,
        // which is exactly what a rejecting visitor must not get.
        if (!isTrackingAllowed()) return;

        if (window.analytics) {
            window.analytics.reset();
            handleAnonId();
            updateSegmentTraitsKey(this.sessionId);
        }
    }

    // ── Getters: identity ────────────────────────────────────────────────

    get sessionId(): string {
        return this.session.getSessionId();
    }

    get userId(): string {
        if (!this._userId) {
            // Falls back to an ephemeral, unpersisted id when first-party measurement is not
            // permitted at all, so features that need *an* id (embed variables, payment
            // return URLs) keep working without writing an identifier to the visitor's
            // device. resolvePersistentUserId() promotes or replaces it from activate().
            this._userId = isFirstPartyTrackingAllowed()
                ? this.readStoredUserId() || nanoid()
                : nanoid();
        }

        return this._userId;
    }

    // ── Getters: campaign config ─────────────────────────────────────────

    get campaignId(): string {
        return this.config.campaignId;
    }

    get companyId(): string {
        return this.config.companyId;
    }

    get workspaceId(): string {
        return this.config.workspaceId;
    }

    get campaignVersionId(): string {
        return this.config.campaignVersionId;
    }

    get trackingVersion(): string | undefined {
        return this.config.trackingVersion;
    }

    // ── Getters: page context ────────────────────────────────────────────

    get pageSlug(): string {
        return this.pageContext?.pageSlug ?? '';
    }

    get pageId(): string {
        return this.pageContext?.pageId ?? '';
    }

    get trackingTitles(): Record<string, string> {
        return this._trackingTitles;
    }

    get variant(): 'A' | 'B' | null {
        return this._variant;
    }

    resolveDisplayData(page: Page): Page {
        if (this._variant === 'B' && page.variantPageData) {
            return {
                ...(page.variantPageData as Page),
                inputMap: page.inputMap,
                pageIdSlugMap: page.pageIdSlugMap,
                context: page.context,
            };
        }
        return page;
    }

    get queryParams(): {
        utm: Record<string, string>;
        custom: Record<string, string>;
        thirdParty: Record<string, string>;
    } {
        return { utm: this._utm, custom: this._custom, thirdParty: this._thirdParty };
    }

    // ── Getters: session / visitor ───────────────────────────────────────

    get visitorProperties(): Partial<Record<string, string>> {
        return this.session.getVisitorProperties();
    }

    // ── Context methods ──────────────────────────────────────────────────

    getEventContext(): Partial<PipelineContext> {
        if (!this.pageContext) {
            return {};
        }
        return serializeForPipeline({
            sessionId: this.sessionId,
            // Must go through the getter — identity is resolved lazily (CAP-4147).
            userId: this.userId,
            config: { ...this.config, trackingTitles: this._trackingTitles },
            page: this.pageContext,
        });
    }

    // ── Version helpers ──────────────────────────────────────────────────

    isV3(): boolean {
        return this.config.trackingVersion === 'v3';
    }

    isV4(): boolean {
        return this.config.trackingVersion === 'v4';
    }

    // ── Composite accessors ──────────────────────────────────────────────

    getSession(): TrackingSession {
        return this.session;
    }

    getStrategy(): TrackingStrategy {
        if (!this.strategy) {
            throw new Error('TrackingStrategy not initialized — call bootstrap() first');
        }
        return this.strategy;
    }
}
